import { useEffect, useRef, useState } from 'react';

interface AnimatedStatCardProps {
  /** Numeric value to count up to */
  numericValue: number;
  /** Prefix shown before the number (e.g. "R$") */
  prefix?: string;
  /** Suffix shown after the number (e.g. "+", "k+", "%") */
  suffix?: string;
  /** Label below the number */
  label: string;
  /** Remix Icon class (e.g. "ri-calendar-event-line") */
  icon?: string;
  /** Animation delay in ms for stagger effect */
  delay?: number;
  /** Whether the parent section is visible (controls entrance animation) */
  visible?: boolean;
  /** CountUp animation duration in ms */
  duration?: number;
  /** Extra className for the card wrapper */
  className?: string;
  /** Text color for the number (default: text-primary) */
  numberColor?: string;
  /** Icon color (default: text-primary) */
  iconColor?: string;
  /** Icon background color (default: bg-primary/10) */
  iconBg?: string;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function useLocalCountUp(
  target: number,
  duration: number,
  started: boolean
): number {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!started) return;
    setCount(0);

    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);

      setCount(Math.round(eased * target));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setCount(target);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, started]);

  return count;
}

/**
 * AnimatedStatCard — counts up from 0 → numericValue when entering viewport.
 *
 * Usage:
 *   <AnimatedStatCard
 *     numericValue={120}
 *     suffix="+"
 *     label="Eventos em BH"
 *     icon="ri-calendar-event-line"
 *     delay={100}
 *     visible={statsVisible}
 *   />
 *
 * The parent section is responsible for the IntersectionObserver via
 * useScrollAnimation. Pass its `isVisible` result as `visible` prop and
 * the component starts counting as soon as that turns true.
 */
export default function AnimatedStatCard({
  numericValue,
  prefix = '',
  suffix = '',
  label,
  icon,
  delay = 0,
  visible = false,
  duration = 1800,
  className = '',
  numberColor = 'text-primary',
  iconColor = 'text-primary',
  iconBg = 'bg-primary/10',
}: AnimatedStatCardProps) {
  const [started, setStarted] = useState(false);
  const count = useLocalCountUp(numericValue, duration, started);

  // Start counting as soon as the card becomes visible
  useEffect(() => {
    if (visible && !started) {
      // Respect the stagger delay before starting the number animation
      const t = setTimeout(() => setStarted(true), delay);
      return () => clearTimeout(t);
    }
  }, [visible, started, delay]);

  return (
    <div
      className={`text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {icon && (
        <div className={`w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full ${iconBg}`}>
          <i className={`${icon} ${iconColor} text-2xl`}></i>
        </div>
      )}

      <div className={`font-heading font-extrabold text-4xl md:text-5xl leading-none mb-2 tabular-nums ${numberColor}`}>
        {prefix}{count}{suffix}
      </div>

      <p className="font-body text-sm text-gray-500">{label}</p>
    </div>
  );
}